import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next'; // Ավելացված է
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPaperPlane, faUsers, faBell, faSync, 
    faHistory, faBullhorn, faCheckCircle, faClock 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminNotifications = () => {
    const { t, i18n } = useTranslation(); // Ավելացված է
    const [users, setUsers] = useState([]);
    const [history, setHistory] = useState([]);
    const [selectedUser, setSelectedUser] = useState('all');
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const getAuthHeaders = useCallback(() => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
    }), []);

    const stats = useMemo(() => ({
        totalUsers: users.length,
        totalSent: history.length,
        todaySent: history.filter(h => new Date(h.created_at).toDateString() === new Date().toDateString()).length
    }), [users, history]);

    const loadInitialData = useCallback(async () => {
        setRefreshing(true);
        const headers = getAuthHeaders();
        try {
            const usersRes = await fetch(`${API_BASE}/api/users`, { headers });
            const usersResult = await usersRes.json();
            
            if (Array.isArray(usersResult)) {
                setUsers(usersResult);
            } else if (usersResult.success && Array.isArray(usersResult.data)) {
                setUsers(usersResult.data);
            } else {
                setUsers([]);
            }

            const historyRes = await fetch(`${API_BASE}/api/notifications`, { headers });
            if (historyRes.ok) {
                const historyResult = await historyRes.json();
                const historyData = Array.isArray(historyResult) ? historyResult : (historyResult.data || []);
                setHistory(historyData);
            }
        } catch (err) {
            console.error("Data Load Error:", err);
        } finally {
            setRefreshing(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => { loadInitialData(); }, [loadInitialData]);

    const handleSend = async (e) => {
        if (e) e.preventDefault();
        if (!title.trim() || !message.trim()) return alert(t('error_fill_fields'));

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/notifications/send-custom-notification`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    userId: selectedUser === 'all' ? null : selectedUser,
                    title,
                    message
                })
            });

            if (response.ok) {
                alert(t('notification_sent_success'));
                setTitle('');
                setMessage('');
                loadInitialData();
            } else if (response.status === 401) {
                alert(t('session_expired_error'));
            } else {
                alert(`${t('error_sending')} (Status: ${response.status})`);
            }
        } catch (error) {
            console.error("Fetch Error:", error);
            alert(t('connection_error'));
        } finally {
            setLoading(false);
        }
    };

    // Ամսաթվի ֆորմատավորում ըստ լեզվի
    const formatDateTime = (dateStr) => {
        const locale = i18n.language === 'am' ? 'hy-AM' : i18n.language === 'ru' ? 'ru-RU' : 'en-US';
        return new Date(dateStr).toLocaleString(locale);
    };

    return (
        <div style={styles.dashboard}>
            <div style={styles.analyticsRow}>
                <AnalyticCard title={t('label_users')} value={stats.totalUsers} icon={faUsers} grad="linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)" />
                <AnalyticCard title={t('label_total_sent')} value={stats.totalSent} icon={faBullhorn} grad="linear-gradient(135deg, #FF9933 0%, #FFB366 100%)" />
                <AnalyticCard title={t('label_today')} value={stats.todaySent} icon={faCheckCircle} grad="linear-gradient(135deg, #01B574 0%, #2AF598 100%)" />
            </div>

            <div style={styles.header}>
                <h1 style={styles.title}>{t('notification_center_title')}</h1>
                <button onClick={loadInitialData} style={styles.refreshCircle}>
                    <FontAwesomeIcon icon={faSync} spin={refreshing} />
                </button>
            </div>

            <div style={styles.mainGrid}>
                <div style={styles.glassCard}>
                    <h3 style={styles.cardTitle}><FontAwesomeIcon icon={faPaperPlane} /> {t('new_message_title')}</h3>
                    <div style={styles.inputBox}>
                        <label style={styles.label}>{t('label_send_to')}</label>
                        <select style={styles.select} value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                            <option value="all">📢 {t('option_all_users')}</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>👤 {u.full_name || u.email} (ID: {u.id})</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.inputBox}>
                        <label style={styles.label}>{t('label_title')}</label>
                        <input style={styles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder={t('placeholder_title')} />
                    </div>

                    <div style={styles.inputBox}>
                        <label style={styles.label}>{t('label_message')}</label>
                        <textarea style={styles.textarea} value={message} onChange={e => setMessage(e.target.value)} placeholder={t('placeholder_message')} />
                    </div>

                    <button onClick={handleSend} disabled={loading} style={loading ? {...styles.sendBtn, opacity: 0.6} : styles.sendBtn}>
                        {loading ? t('sending_loader') : t('btn_send_notification')}
                    </button>
                </div>

                <div style={styles.glassCard}>
                    <h3 style={styles.cardTitle}><FontAwesomeIcon icon={faHistory} /> {t('recent_activities_title')}</h3>
                    <div style={styles.historyContainer}>
                        {history.length === 0 ? (
                            <div style={styles.emptyState}>{t('history_empty')}</div>
                        ) : (
                            history.map(h => (
                                <div key={h.id} style={styles.historyCard}>
                                    <div style={styles.historyIcon}><FontAwesomeIcon icon={faBell} /></div>
                                    <div style={{flex: 1}}>
                                        <h4 style={styles.historyTitle}>{h.title}</h4>
                                        <p style={styles.historyMsg}>{h.message}</p>
                                        <div style={styles.historyMeta}>
                                            <FontAwesomeIcon icon={faClock} /> {formatDateTime(h.created_at)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const AnalyticCard = ({ title, value, icon, grad }) => (
    <div style={{...styles.anaCard, background: grad}}>
        <div style={styles.anaIconBox}><FontAwesomeIcon icon={icon} /></div>
        <div>
            <p style={styles.anaLabel}>{title}</p>
            <h2 style={styles.anaVal}>{value}</h2>
        </div>
    </div>
);
// styles remain the same...


const styles = {
    // ... (ոճերը նույնն են մնում)
    dashboard: { padding: '30px', backgroundColor: '#F4F7FE', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif" },
    analyticsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '40px' },
    anaCard: { padding: '25px', borderRadius: '24px', color: '#fff', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 10px 20px rgba(67, 24, 255, 0.15)' },
    anaIconBox: { width: '45px', height: '45px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' },
    anaLabel: { fontSize: '11px', margin: 0, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' },
    anaVal: { fontSize: '26px', margin: '5px 0 0 0', fontWeight: '800' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
    title: { fontSize: '24px', fontWeight: '800', color: '#1B2559', margin: 0 },
    refreshCircle: { width: '45px', height: '45px', borderRadius: '50%', border: 'none', background: '#fff', color: '#4318FF', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    mainGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px' },
    glassCard: { background: '#fff', borderRadius: '24px', padding: '30px', boxShadow: '0 10px 30px rgba(0,0,0,0.02)', border: '1px solid #f1f5f9' },
    cardTitle: { fontSize: '18px', fontWeight: '800', color: '#1B2559', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' },
    inputBox: { marginBottom: '20px' },
    label: { display: 'block', fontSize: '12px', fontWeight: '700', color: '#A3AED0', marginBottom: '8px', marginLeft: '5px' },
    input: { width: '100%', padding: '14px', borderRadius: '14px', border: '1px solid #E0E5F2', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
    select: { width: '100%', padding: '14px', borderRadius: '14px', border: '1px solid #E0E5F2', fontSize: '14px', background: '#F4F7FE' },
    textarea: { width: '100%', padding: '14px', borderRadius: '14px', border: '1px solid #E0E5F2', fontSize: '14px', minHeight: '120px', boxSizing: 'border-box', outline: 'none' },
    sendBtn: { width: '100%', padding: '16px', borderRadius: '16px', background: '#4318FF', color: '#fff', border: 'none', fontWeight: '800', fontSize: '15px', cursor: 'pointer', boxShadow: '0 10px 20px rgba(67, 24, 255, 0.25)', transition: '0.3s' },
    historyContainer: { maxHeight: '450px', overflowY: 'auto', paddingRight: '10px' },
    historyCard: { display: 'flex', gap: '15px', padding: '15px', background: '#F4F7FE', borderRadius: '18px', marginBottom: '15px', border: '1px solid #E0E5F2' },
    historyIcon: { width: '40px', height: '40px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FF9933', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' },
    historyTitle: { fontSize: '15px', fontWeight: '700', color: '#1B2559', margin: '0 0 5px 0' },
    historyMsg: { fontSize: '13px', color: '#475569', margin: '0 0 8px 0', lineHeight: '1.4' },
    historyMeta: { fontSize: '11px', color: '#A3AED0', fontWeight: '600' },
    emptyState: { textAlign: 'center', color: '#A3AED0', padding: '40px' }
};

export default AdminNotifications;